import { Button } from '../atoms';
import type { Column, TableAction } from '../../types/table';

/**
 * TableRow Component - Molecule Level
 * 
 * Renders a single table row with cells and actions
 * 
 * Features:
 * - Dynamic cell rendering
 * - Row selection
 * - Action buttons
 * - Hover effects
 * - Click handlers
 * - Responsive
 */

export interface TableRowProps<TData = any> {
  /** Row data */
  row: TData;
  /** Column definitions */
  columns: Column<TData>[];
  /** Row index */
  index: number;
  /** Whether row is selected */
  selected?: boolean;
  /** Callback when row selection changes */
  onSelectionChange?: () => void;
  /** Whether row selection is enabled */
  hasSelection?: boolean;
  /** Action buttons */
  actions?: TableAction<TData>[];
  /** Callback when row is clicked */
  onRowClick?: (row: TData) => void;
  /** Whether to show hover effect */
  hoverable?: boolean;
  /** Whether to use striped styling */
  striped?: boolean;
  /** Custom row class name */
  className?: string;
}

export const TableRow = <TData extends Record<string, any>>({
  row,
  columns,
  index,
  selected = false,
  onSelectionChange,
  hasSelection = false,
  actions = [],
  onRowClick,
  hoverable = true,
  striped = false,
  className = '',
}: TableRowProps<TData>) => {
  /**
   * Get cell value from row data
   */
  const getCellValue = (column: Column<TData>, row: TData) => {
    if (column.accessor) {
      if (typeof column.accessor === 'function') {
        return column.accessor(row);
      }
      return row[column.accessor];
    }
    return null;
  };

  /**
   * Render cell content
   */
  const renderCell = (column: Column<TData>, row: TData) => {
    const value = getCellValue(column, row);
    
    if (column.cell) {
      return column.cell(row, value);
    }
    
    // Default rendering
    if (value === null || value === undefined) {
      return <span className="text-gray-400">-</span>;
    }
    
    return value;
  };

  /**
   * Get alignment class
   */
  const getAlignmentClass = (align?: 'left' | 'center' | 'right') => {
    switch (align) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      default:
        return 'text-left';
    }
  };

  const rowClasses = `
    ${hoverable ? 'hover:bg-gray-50 transition-colors' : ''}
    ${striped && index % 2 === 1 ? 'bg-gray-50/50' : ''}
    ${selected ? 'bg-blue-50' : ''}
    ${onRowClick ? 'cursor-pointer' : ''}
    ${className}
  `.trim();

  return (
    <tr className={rowClasses} onClick={() => onRowClick?.(row)}>
      {/* Selection Checkbox */}
      {hasSelection && (
        <td className="px-6 py-4 w-12">
          <div className="flex items-center justify-center">
            <input
              type="checkbox"
              checked={selected}
              onChange={onSelectionChange}
              onClick={(e) => e.stopPropagation()} // Prevent row click
              className="w-4 h-4 text-[#EAB308] bg-white border-gray-300 rounded focus:ring-[#EAB308] focus:ring-2 cursor-pointer"
              aria-label={`Select row ${index + 1}`}
            />
          </div>
        </td>
      )}

      {/* Data Cells */}
      {columns.map((column) => (
        <td
          key={column.id}
          className={`px-6 py-4 text-sm text-gray-900 ${getAlignmentClass(column.align)} ${
            column.hideOnMobile ? 'hidden md:table-cell' : ''
          } ${column.cellClassName || ''}`}
          style={{
            width: column.width,
            minWidth: column.minWidth,
          }}
        >
          {renderCell(column, row)}
        </td>
      ))}

      {/* Actions Cell */}
      {actions.length > 0 && (
        <td className="px-6 py-4 text-right text-sm">
          <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
            {actions.map((action) => {
              // Check if action should be shown
              if (action.show && !action.show(row)) {
                return null;
              }

              // Check if action is disabled
              const isDisabled = action.disabled ? action.disabled(row) : false;

              return (
                <Button
                  key={action.id}
                  size="sm"
                  variant={action.variant || 'ghost'}
                  onClick={() => action.onClick(row)}
                  disabled={isDisabled}
                  leftIcon={action.icon}
                  ariaLabel={action.label}
                >
                  {action.label}
                </Button>
              );
            })}
          </div>
        </td>
      )}
    </tr>
  );
};

export default TableRow;

